import {noop, groupByUniqueKey} from '@benchling/js-utils';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {MinimalFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as FileSelectorUtil from 'file-browser/FileSelectorUtil';
import FolderDAOCache from 'file-browser/FolderDAOCache';
// @ts-expect-error
import GetFoldersWrapper from 'file-browser/GetFoldersWrapper';
import makeCancelable from 'lib/makeCancelable';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {Filter, FilterKey, Operator, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import useOnChangeOnly from 'ui-library/hooks/useOnChangeOnly';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import TextInput from 'ui-library/inputs/TextInput';
import ControlledSelectTree from 'ui-library/select-tree/ControlledSelectTree';
import {TreeItem, TreeItemStateMap} from 'ui-library/select-tree/SelectTreeConstants';
import {sortedRootItems} from 'ui-library/select-tree/SelectTreeUtil';
import APIUtil from 'util/APIUtil';
import {InputChangeEvent} from 'util/Types';

import {BULK_ASSEMBLY_FOLDER_FILTER} from '../BulkAssemblyConstants';

interface SearchableBySequenceFileTreeProps {
  fetchLimit: number;
  folders: Array<MinimalFolder>;
  selectedIds: Array<string>;
  onChangeSelectedIds: (selectedIds: Array<string>) => void;
}

const SEQUENCE_OR_FOLDER_FILTERS: Array<Filter> = [
  {
    key: FilterKey.TYPES,
    operator: Operator.IS_ONE_OF,
    value: [ModelTypeConstants.FOLDER, ModelTypeConstants.SEQUENCE],
  },
  {
    key: FilterKey.ARCHIVE_PURPOSES,
    operator: Operator.IS_ONE_OF,
    value: [NOT_ARCHIVED_PURPOSE_STRING],
  },
];

const makeFolderFilter = (id: string): Filter => ({key: FilterKey.FOLDER, operator: Operator.IS, value: id});

interface FolderIDAndResponse {
  folderId: string;
  searchResponse: GlobalSearchResponse;
}

interface CachedSearch {
  results: GlobalSearchResponse;
  query: string;
  offset: number;
}

export const SearchableBySequenceFileTree = (props: SearchableBySequenceFileTreeProps): JSX.Element => {
  const [query, setQuery] = useState('');
  const folderTreeItemsById = useMemo(
    () =>
      groupByUniqueKey(FileSelectorUtil.foldersToSelectTreeItems(props.folders), (treeItem) => treeItem.id),
    [props.folders]
  );
  const [rootItems, setRootItems] = useState(() =>
    sortedRootItems(
      FileSelectorUtil.foldersToSelectTreeItems(props.folders).filter((treeItem) => !treeItem.parentId)
    )
  );
  const [cachedSearch, setCachedSearch] = useState<CachedSearch>();

  const getInitialItemStateMap = useCallback((): TreeItemStateMap => {
    const newItemStateMap: TreeItemStateMap = {};
    props.folders.forEach((folder) => {
      newItemStateMap[folder.api_identifier] = {
        baseLeafItems: [],
        childItems: [],
        isExpanded: false,
        leafItems: [],
        loading: false,
        more: true,
        offset: 0,
      };
    });
    props.folders.forEach((folder) => {
      if (folder.parent?.api_identifier) {
        newItemStateMap[folder.parent.api_identifier].childItems.push(
          folderTreeItemsById[folder.api_identifier]
        );
      }
    });
    return newItemStateMap;
  }, [props.folders, folderTreeItemsById]);

  const [itemStateMap, setItemStateMap] = useState<TreeItemStateMap>(getInitialItemStateMap);
  const [clickedItems, setClickedItems] = useState<Array<string>>([]);
  const [rootOffset, setRootOffset] = useState(0);
  const [moreRootItems, setMoreRootItems] = useState(false);
  const [inFlightRootItemQueries, setInFlightRootItemQueries] = useState(0);

  const loadMoreFromQuery = useCallback(
    (updatedQuery: string, initialRootItems: Array<TreeItem>, offset: number) => {
      const handleSearchResponse = (response: GlobalSearchResponse): void => {
        setRootOffset(offset + props.fetchLimit);
        setMoreRootItems(offset + props.fetchLimit < response.total);
        const knownFolderIds = Object.keys(folderTreeItemsById);
        const foundFolderIds = Object.keys(response.foldersById);
        foundFolderIds
          .filter((id) => !knownFolderIds.includes(id))
          .forEach((id) => {
            // Invalidating the folder id forces the GetFoldersWrapper component to refetch folders, updating
            //  props.folders for this component.
            FolderDAOCache.invalidateFolder(id);
          });
        const newRootItems = response.ids.map((id) => {
          if (APIUtil.isNucleotideSequence(id)) {
            return FileSelectorUtil.fileToSelectTreeItem(response.fileSummariesById[id]);
          } else {
            return folderTreeItemsById[id];
          }
        });
        // Skip null items (from missing folders in folderTreeItemsById) to avoid an error until props.folders
        //  is updated.
        setRootItems([...initialRootItems, ...newRootItems.filter((i) => i)]);
      };
      // If this was triggered by folder cache invalidation, no need to re-request search results.
      if (cachedSearch != null && cachedSearch.query === updatedQuery && cachedSearch.offset === offset) {
        handleSearchResponse(cachedSearch.results);
        return noop;
      }
      const params: SearchParams = {
        offset,
        limit: props.fetchLimit,
        sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
        filters: [...SEQUENCE_OR_FOLDER_FILTERS],
        query: updatedQuery,
      };
      setInFlightRootItemQueries((count) => count + 1);
      const {promise, cancel} = makeCancelable(
        GlobalSearchActions.doGlobalSearch(params, SearchType.GLOBAL, 'assembly:SEQUENCE_FILE_TREE_SEARCH')
      );
      const handleSearch = async (): Promise<void> => {
        try {
          const response = (await promise) as GlobalSearchResponse;
          setCachedSearch({results: response, query: updatedQuery, offset});
          handleSearchResponse(response);
        } catch (err) {
          // Noop when the promise is canceled.
          if (!err.isCanceled) {
            throw err;
          }
        } finally {
          setInFlightRootItemQueries((count) => count - 1);
        }
      };
      handleSearch();
      return cancel;
    },
    [cachedSearch, props.fetchLimit, folderTreeItemsById]
  );

  // Reset the item map when folders change.
  useEffect(() => {
    setItemStateMap(getInitialItemStateMap());
  }, [getInitialItemStateMap]);

  // When the root query changes, cleanup any ongoing searches and make a new search.
  useOnChangeOnly(() => {
    let cancelSearch: () => void = noop;
    // Do not reset if the search for this query has already been cached.
    if ((!query || query.length >= 3) && cachedSearch?.query !== query) {
      setRootItems([]);
      setRootOffset(0);
      setMoreRootItems(true);
      cancelSearch = loadMoreFromQuery(query, [], 0);
    }
    return () => {
      // Cancel in-flight search request if there is one.
      cancelSearch();
    };
  }, [query, loadMoreFromQuery, cachedSearch]);

  // do a separate query for each id, set loading state for each, and update itemStateMap
  const loadMoreFromIds = (ids: Array<string>, initialStateMap: TreeItemStateMap = itemStateMap): void => {
    const newItemStateMap: TreeItemStateMap = {...initialStateMap};
    const fetchPromises: Array<
      Promise<{
        folderId: string;
        searchResponse: GlobalSearchResponse;
      }>
    > = ids
      .map((id) => ({id, oldItemState: initialStateMap[id]}))
      .filter(({id, oldItemState}) => oldItemState && oldItemState.isExpanded && oldItemState.more)
      .map(({id, oldItemState}) => {
        newItemStateMap[id].loading = true;
        const params: SearchParams = {
          offset: oldItemState.offset,
          limit: props.fetchLimit,
          sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
          filters: [...SEQUENCE_OR_FOLDER_FILTERS, makeFolderFilter(id)],
          // `query` is intentionally empty -- for `loadMoreFromIds`, we want to load all descendants of a
          // given folder, not just the ones that match the query. The query is only used when loading global
          // matches in `loadMoreFromQuery`.
          query: '',
        };
        const fetchPromise = GlobalSearchActions.doGlobalSearch(
          params,
          SearchType.GLOBAL,
          'assembly:SEQUENCE_FILE_TREE_CHILDREN'
        ).then((response) => ({folderId: id, searchResponse: response as GlobalSearchResponse}));
        return fetchPromise;
      });
    setItemStateMap(newItemStateMap);
    Promise.allSettled(fetchPromises).then(
      // @ts-expect-error
      (promiseResponses: Array<{status: string; value: FolderIDAndResponse}>) => {
        promiseResponses.forEach((promiseResponse) => {
          const {folderId, searchResponse} = promiseResponse.value;
          newItemStateMap[folderId].loading = false;
          newItemStateMap[folderId].more =
            newItemStateMap[folderId].offset + props.fetchLimit < searchResponse.total;
          newItemStateMap[folderId].offset += props.fetchLimit;
          searchResponse.ids.forEach((id) => {
            if (APIUtil.isNucleotideSequence(id)) {
              newItemStateMap[folderId].leafItems.push(
                FileSelectorUtil.fileToSelectTreeItem(searchResponse.fileSummariesById[id])
              );
            } else if (!newItemStateMap[folderId].childItems.some((item) => item.id === id)) {
              newItemStateMap[folderId].childItems.push(folderTreeItemsById[id]);
              newItemStateMap[id] = {
                baseLeafItems: [],
                childItems: [],
                isExpanded: false,
                leafItems: [],
                loading: false,
                more: true,
                offset: 0,
              };
            }
          });
        });
        // Copy the new itemStateMap so React recognizes that the state has changed.
        setItemStateMap({...newItemStateMap});
      }
    );
  };

  return (
    <div>
      <TextInput
        disabled={false}
        onChange={(e: InputChangeEvent) => {
          setQuery(e.target.value);
        }}
        value={query}
      />
      {query && query.length < 3 ? (
        <div>Please enter a query with at least 3 characters.</div>
      ) : (
        <ControlledSelectTree
          allowNone={false}
          canLoadMore={() => true}
          moreRootItems={moreRootItems}
          clickedItems={clickedItems}
          handleClickedItemsChange={(newClickedItem) => {
            setClickedItems([...clickedItems, newClickedItem]);
          }}
          hasQuery={false}
          itemTypeName="sequences"
          itemStateMap={itemStateMap}
          isLoadingRootItems={inFlightRootItemQueries > 0}
          loadMoreFromIds={loadMoreFromIds}
          multiple={true}
          onChangeSelectedIds={props.onChangeSelectedIds}
          onLoadMoreRootItems={() => {
            loadMoreFromQuery(query, rootItems, rootOffset);
          }}
          rootItems={rootItems}
          selectAllText="All sequences"
          selectedIds={props.selectedIds}
          toggleExpanded={(itemId) => {
            const willBeExpanded = !itemStateMap[itemId].isExpanded;
            const newItemStateMap = {...itemStateMap};
            newItemStateMap[itemId].isExpanded = willBeExpanded;
            setItemStateMap(newItemStateMap);
            if (
              willBeExpanded &&
              itemStateMap[itemId].more &&
              itemStateMap[itemId].childItems.filter((childItem) =>
                APIUtil.isNucleotideSequence(childItem.id)
              ).length === 0
            ) {
              loadMoreFromIds([itemId], newItemStateMap);
            }
          }}
        />
      )}
    </div>
  );
};

export default function SearchableBySequenceFileTreeWrapper(
  props: Omit<SearchableBySequenceFileTreeProps, 'folders'>
): JSX.Element {
  return (
    <GetFoldersWrapper folderFilter={{...BULK_ASSEMBLY_FOLDER_FILTER, showNotebook: true}}>
      {(folders: Array<MinimalFolder> | null) =>
        !folders ? (
          <div className="u-small-padding-bottom text-center">
            <HorizontalThrobber />
          </div>
        ) : (
          <SearchableBySequenceFileTree folders={folders} {...props} />
        )
      }
    </GetFoldersWrapper>
  );
}
